import React, { useState } from "react";
import * as dayjs from "dayjs";
import { Button } from "@material-ui/core";
import { ReactRender } from "@/components/ReactRender";
import { useTranslation } from "@/langs/useTranslation";
import { approveCMSResource, getCMSResourceList } from "@/apis/apiCollection";
import modalConfirm from "@/components/Modal/modalConfirm";
import DataTable from "@/components/DataTable";
import { formatSecondsToTime } from "@/helpers/utils";
import { IconPreview } from "@/components/Icon";
import { downloadFileLink } from "@/apis/announcementApi";
import MediaShow from "./MediaShow";
import HasPermission from "@/components/commons/HasPermission";
import { isFecAdmin } from "@/apis/getUserInfo";
import { APP_ENV } from "@/APP_ENV";

export const CMSResourceDatatable = React.forwardRef(
  ({ getSearchParams, children, onDelete }, ref) => {
    const { t } = useTranslation();
    const [previewOpen, setPreviewOpen] = useState(false);
    const [mediaSrc, setMediaSrc] = useState("");
    const [mediaType, setMediaType] = useState("");

    const handlePreview = async (s3Path, type) => {
      const { download_link: src } = await downloadFileLink(s3Path);
      setPreviewOpen(true);
      setMediaSrc(src);
      setMediaType((type || "").split("/")[0]);
    };

    let needsManualApproval = true;
    if (!APP_ENV.REACT_APP_CMS_AUTO_APPROVAL) {
      needsManualApproval =
        isFecAdmin() && !APP_ENV.REACT_APP_CMS_AUTO_APPROVAL;
    }

    const columns = [
      {
        data: "name",
        title: t({ defaultMessage: "资源名称" }),
        searchable: true,
        orderable: false
      },
      {
        data: "s3Path",
        title: "",
        orderable: false,
        createdCell: (td, data, row) => {
          ReactRender(
            <IconPreview
              size={16}
              onClick={() => handlePreview(data, row.mimeType)}
            />,
            td
          );
        }
      },
      {
        data: "mimeType",
        title: t({ defaultMessage: "类型" }),
        orderable: false,
        render: data => {
          if (data.indexOf("/") >= 0) return data.split("/")[1];
          return data;
        }
      },
      {
        data: "width",
        title: t({ defaultMessage: "尺寸" }),
        orderable: false,
        render: (data, _, row) => {
          return `${row.width}*${row.height}`;
        }
      },
      {
        data: "size",
        title: t({ defaultMessage: "大小" }),
        orderable: false,
        render: data => {
          return `${(data / 1048576).toFixed(2)} MB`;
        }
      },
      {
        data: "duration",
        title: t({ defaultMessage: "时长" }),
        orderable: false,
        render: data => {
          if (data) return formatSecondsToTime(data);
          return "";
        }
      },
      {
        data: "creatorName",
        title: t({ defaultMessage: "上传者" }),
        orderable: false
      },
      {
        data: "createdAt",
        title: t({ defaultMessage: "上传时间" }),
        orderable: false,
        render: data => {
          return dayjs(Number(data)).format("YYYY/MM/DD HH:mm:ss");
        }
      },
      {
        data: "status",
        title: t({ defaultMessage: "状态" }),
        orderable: false,
        createdCell: (td, data, row) => {
          let Compo;
          if (data === 1 || data === 2)
            Compo = (
              <div style={{ color: "#5258DD" }}>
                {t({ defaultMessage: "审核中" })}
              </div>
            );
          if (data === 3)
            Compo = (
              <div style={{ color: "#00B42A" }}>
                {t({ defaultMessage: "审核通过" })}
              </div>
            );
          if (data === 4)
            Compo = (
              <div style={{ color: "#F53F3F" }}>
                {t({ defaultMessage: "审核失败" })}
              </div>
            );
          ReactRender(Compo, td);
        }
      },
      {
        data: null,
        title: t({ defaultMessage: "操作" }),
        className: "center nowrap",
        searchable: false,
        orderable: false,
        width: 120,
        createdCell: (td, data, row) => {
          ReactRender(
            <>
              <HasPermission permission="resource_library_delete_resource">
                <Button
                  size="small"
                  color="secondary"
                  onClick={() => {
                    onDelete(row);
                  }}
                >
                  {t({ defaultMessage: "删除" })}
                </Button>
              </HasPermission>
              {row.status === 4 && needsManualApproval && (
                <Button
                  size="small"
                  color="primary"
                  onClick={() => {
                    modalConfirm({
                      title: t({ defaultMessage: "批准提醒" }),
                      content: t({
                        defaultMessage:
                          "本平台上傳的數位內容將經由 Amazon Rekognition內容審核 自動審查，以降低公開發佈的風險。若自動審查未通過，您可選擇手動審核通過；但在此情況下，本平台將不對任何後續影響承擔責任。"
                      }),
                      onConfirm: async () => {
                        await approveCMSResource({ id: row.id });
                        ref.current.reload();
                      }
                    });
                  }}
                >
                  {t({ defaultMessage: "手动批准" })}
                </Button>
              )}
            </>,
            td
          );
        }
      }
    ];

    const ajax = (data, callback) => {
      return getCMSResourceList({
        ...data,
        ...getSearchParams()
      }).then(res => {
        callback({
          data: res.contentResources,
          // draw: data.draw,
          recordsFiltered: res.total,
          recordsTotal: res.total
        });
      });
    };

    return (
      <>
        <DataTable
          ref={ref}
          ordering
          columns={columns}
          ajax={ajax}
          scrollable
          showCheckAll={false}
          // autoLoad
        >
          {children}
        </DataTable>
        <MediaShow
          type={mediaType}
          src={mediaSrc}
          open={previewOpen}
          onClose={() => setPreviewOpen(false)}
        />
      </>
    );
  }
);
